import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  TextField,
  Pagination,
  InputAdornment,
  CircularProgress,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import axiosInstance from "../../../../api/axiosConfig";
import { faNairaSign } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TokenDetailsModal from "./TokenDetailsModal"; // Import modal
import { toast, ToastContainer } from "react-toastify";

function Tokens() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(6); // Items per page
  const [searchTerm, setSearchTerm] = useState("");

  // Modal State
  const [selectedTokenId, setSelectedTokenId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchTokens();
  }, [page]);

  const fetchTokens = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        "/marketplace/getequity/get-tokens/",
        {
          params: { page, limit },
        },
      );

      const data = response.data?.data || [];
      const pages = response.data?.totalPages || 1;

      setTokens(data);
      setTotalPages(pages);
    } catch (error) {
      toast.error(error.response?.data?.message);
      console.error("Error fetching tokens:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTokenClick = (token) => {
    if (token?._id) {
      setSelectedTokenId(token._id);
      setIsModalOpen(true);
    } else {
      toast.error("No Id found");
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTokenId(null);
  };

  // Client-side filtering across name, interest, and investment_category
  const filteredTokens = tokens.filter((token) => {
    const term = searchTerm.toLowerCase();
    return (
      token.name?.toLowerCase().includes(term) ||
      token.investment_category?.toLowerCase().includes(term) ||
      token.interest?.toString().includes(term) ||
      token.tenor?.toString().includes(term)
    );
  });

  return (
    <Box sx={{ color: "#000000" }}>
      {/* Search & Filter Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          size="small"
          placeholder="Search by name, category, or interest..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          sx={{
            maxWidth: 400,
            "& .MuiOutlinedInput-root": {
              borderRadius: 0,
              bgcolor: "#ffffff",
              "& fieldset": { borderColor: "#000000" },
              "&:hover fieldset": { borderColor: "#000000" },
              "&.Mui-focused fieldset": { borderColor: "#000000" },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#000000" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Grid Display */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: "#000000" }} />
        </Box>
      ) : filteredTokens.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ textAlign: "center", py: 4, color: "#666666" }}
        >
          No assets found.
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {filteredTokens.map((token, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                onClick={() => handleTokenClick(token)}
                sx={{
                  border: "1px solid #000000",
                  borderRadius: 0,
                  boxShadow: "none",
                  cursor: "pointer",
                  transition: "transform 0.15s ease, box-shadow 0.15s ease",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                {/* Header Image */}
                <CardMedia
                  component="img"
                  height="140"
                  image={token.image}
                  alt={token.name}
                  sx={{
                    objectFit: "contain",
                    bgcolor: "#f9f9f9",
                    p: 2,
                    borderBottom: "1px solid #e0e0e0",
                  }}
                />

                <CardContent sx={{ p: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: "uppercase",
                      fontWeight: 700,
                      color: "#666666",
                    }}
                  >
                    {token.investment_category}
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: "bold",
                      lineHeight: 1.2,
                      mt: 0.5,
                      mb: 1.5,
                      minHeight: 42,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {token.name}
                  </Typography>

                  <Divider sx={{ mb: 1.5 }} />

                  {/* Key Stats */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.8,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Interest Rate
                    </Typography>
                    <Typography variant="caption" fontWeight="bold">
                      {token.interest}%
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.8,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Unit Price
                    </Typography>
                    <Typography variant="caption" fontWeight="bold">
                      <FontAwesomeIcon icon={faNairaSign} />
                      {token.price?.buy?.toLocaleString()}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.8,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Tenor
                    </Typography>
                    <Typography variant="caption" fontWeight="bold">
                      {token.tenor} days
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.8,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Min / Max Limit
                    </Typography>
                    <Typography variant="caption" fontWeight="bold">
                      <FontAwesomeIcon icon={faNairaSign} />{" "}
                      {(token.min_trade?.buy / 1000).toFixed(0)}k -{" "}
                      {(token.max_trade?.buy / 1000000).toFixed(0)}M
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Rating
                    </Typography>
                    <Typography variant="caption" fontWeight="bold">
                      {token.rating || "-"}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination Footer */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            shape="rounded"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: 0,
                color: "#000000",
                "&.Mui-selected": {
                  bgcolor: "#000000",
                  color: "#ffffff",
                  "&:hover": { bgcolor: "#333333" },
                },
              },
            }}
          />
        </Box>
      )}

      {/* Detail Modal */}
      <TokenDetailsModal
        open={isModalOpen}
        onClose={handleCloseModal}
        tokenId={selectedTokenId}
      />

      <ToastContainer />
    </Box>
  );
}

export default Tokens;
